import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, DatePicker, Input, Button, Table, message, Modal, Space } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, EyeOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const { Option } = Select;
const { RangePicker } = DatePicker;

const getToken = () => localStorage.getItem('notes_token') || '';
const getCurrentUser = () => localStorage.getItem('notes_user') || '';

const fetchWithAuth = async (url, options = {}) => {
  const token = getToken();
  const res = await fetch(url, {
    ...options,
    headers: { ...options.headers, 'x-token': token }
  });
  if (res.status === 401) {
    localStorage.removeItem('notes_token');
    localStorage.removeItem('notes_user');
    localStorage.removeItem('notes_realname');
    alert('登录超时，请重新登录');
    window.location.href = '/';
  }
  return res;
};

// 格式化文件大小
const formatSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

export default function WorkLogAttachments() {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const isAdmin = currentUser === 'jch';

  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(false);

  // 加载项目组
  useEffect(() => {
    loadGroups();
  }, []);

  // 加载附件列表
  useEffect(() => {
    loadAttachments();
  }, [selectedGroup, dateRange, keyword, page]);

  const loadGroups = async () => {
    try {
      const resp = await fetchWithAuth(`${API_URL}/api/work-groups`);
      const data = await resp.json();
      if (Array.isArray(data)) {
        setGroups(data);
      }
    } catch (e) {
      console.error('加载项目组失败', e);
    }
  };

  const loadAttachments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedGroup) params.append('groupId', selectedGroup);
      if (dateRange && dateRange[0]) params.append('startDate', dateRange[0].format('YYYY-MM-DD'));
      if (dateRange && dateRange[1]) params.append('endDate', dateRange[1].format('YYYY-MM-DD'));
      if (keyword) params.append('keyword', keyword);
      params.append('page', page);
      params.append('pageSize', pageSize);

      const resp = await fetchWithAuth(`${API_URL}/api/work-log-attachments?${params.toString()}`);
      const data = await resp.json();
      setAttachments(data.data || []);
      setTotal(data.total || 0);
    } catch (e) {
      message.error('加载附件列表失败');
    }
    setLoading(false);
  };

  const handleSearch = () => {
    setPage(1);
    loadAttachments();
  };

  const handleReset = () => {
    setSelectedGroup(null);
    setDateRange(null);
    setKeyword('');
    setPage(1);
  };

  const handleDelete = (record) => {
    // 权限检查：仅日志所有者和管理员可删除
    const isOwner = record.user_name === currentUser;
    if (!isOwner && !isAdmin) {
      message.warning('仅日志所有者和管理员可删除附件');
      return;
    }

    Modal.confirm({
      title: '确认删除',
      content: `确定删除文件 "${record.filename}" 吗？此操作不可恢复。`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await fetchWithAuth(`${API_URL}/api/work-log-attachments/${record.id}`, {
            method: 'DELETE'
          });
          if (res.ok) {
            message.success('删除成功');
            loadAttachments();
          } else {
            const data = await res.json().catch(() => ({}));
            message.error(data.error || '删除失败');
          }
        } catch (e) {
          message.error('删除失败');
        }
      }
    });
  };

  const handleDownload = (record) => {
    const token = getToken();
    const url = record.url.startsWith('http') ? record.url : `${API_URL}${record.url}`;
    // 添加token参数用于下载鉴权
    const separator = url.includes('?') ? '&' : '?';
    window.open(`${url}${separator}token=${token}`, '_blank');
  };

  const handlePreview = (record) => {
    // 图片/PDF直接新窗口预览，其他文件尝试下载
    const mime = record.mime_type || '';
    if (mime.startsWith('image/') || mime === 'application/pdf') {
      const token = getToken();
      const url = record.url.startsWith('http') ? record.url : `${API_URL}${record.url}`;
      const separator = url.includes('?') ? '&' : '?';
      window.open(`${url}${separator}token=${token}`, '_blank');
    } else {
      message.info('该文件类型不支持在线预览，请下载后查看');
      handleDownload(record);
    }
  };

  const columns = [
    {
      title: '文件名',
      dataIndex: 'filename',
      key: 'filename',
      width: 280,
      ellipsis: true,
      render: (text, record) => (
        <a onClick={() => handlePreview(record)} title={text} style={{ color: '#1890ff' }}>
          {text}
        </a>
      )
    },
    {
      title: '大小',
      dataIndex: 'size',
      key: 'size',
      width: 100,
      render: (size) => formatSize(size)
    },
    {
      title: '所属日志',
      key: 'log_info',
      width: 260,
      render: (_, record) => (
        <span>
          {record.log_date} · {record.log_project} · {record.user_name}
        </span>
      )
    },
    {
      title: '上传时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, record) => {
        const isOwner = record.user_name === currentUser;
        const canDelete = isOwner || isAdmin;
        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
            >
              下载
            </Button>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handlePreview(record)}
            >
              预览
            </Button>
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
              >
                删除
              </Button>
            )}
          </Space>
        );
      }
    }
  ];

  return (
    <div className="dashboard-container">
      <div className="dashboard-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/work-logs')}>返回</Button>
        <h2 style={{ margin: 0 }}>📎 附件总览</h2>
      </div>

      {/* 筛选条件 */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 16,
        marginBottom: 16,
        padding: 16,
        background: '#fff',
        borderRadius: 8,
        border: '1px solid #e8e8e8'
      }}>
        <Select
          placeholder="选择项目组"
          allowClear
          style={{ width: 200 }}
          value={selectedGroup}
          onChange={setSelectedGroup}
        >
          {groups.map(g => (
            <Option key={g.id} value={g.id}>{g.name}</Option>
          ))}
        </Select>

        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          placeholder={['开始日期', '结束日期']}
        />

        <Input
          placeholder="搜索文件名"
          prefix={<SearchOutlined />}
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 200 }}
          allowClear
        />

        <Button type="primary" onClick={handleSearch}>搜索</Button>
        <Button onClick={handleReset}>重置</Button>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={attachments}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: false,
          showTotal: (total) => `共 ${total} 个附件`,
          onChange: (p) => setPage(p)
        }}
        size="middle"
      />
    </div>
  );
}
